import { useCallback, useMemo } from "react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";

interface Option {
  id?: string | null;
  name?: string | null;
  description?: string | null;
}

export interface CheckboxListFilterProps<T extends Option> {
  options: (T | null | undefined)[];
  loading?: boolean;
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  emptyMessage?: string;
  skeletonCount?: number;
}

export function CheckboxListFilter<T extends Option>({
  options,
  loading = false,
  selectedIds,
  onChange,
  emptyMessage = "No options available",
  skeletonCount = 2,
}: CheckboxListFilterProps<T>) {
  const validOptions = useMemo(
    () =>
      options.filter(
        (opt): opt is T & { id: string; name: string } =>
          opt != null && !!opt.id && !!opt.name
      ),
    [options]
  );

  const handleToggle = useCallback(
    (optionId: string, checked: boolean) => {
      const newIds = checked
        ? [...selectedIds, optionId]
        : selectedIds.filter((id) => id !== optionId);
      onChange(newIds);
    },
    [selectedIds, onChange]
  );

  if (loading) {
    return (
      <Stack spacing={1}>
        {Array.from({ length: skeletonCount }, (_, i) => (
          <Skeleton key={i} variant="rectangular" height={32} />
        ))}
      </Stack>
    );
  }

  if (validOptions.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyMessage}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        columnGap: 2,
        rowGap: 0.5,
      }}
    >
      {validOptions.map((option) => {
        const control = (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={selectedIds.includes(option.id)}
                onChange={(e) => handleToggle(option.id, e.target.checked)}
                data-testid="scoring-option-checkbox-btn"
                sx={{ py: 0.25 }}
              />
            }
            label={<Typography variant="body2">{option.name}</Typography>}
            sx={{
              mr: 0,
              my: 0,
              "& .MuiFormControlLabel-label": { ml: 0.5 },
            }}
          />
        );
        return option.description ? (
          <Tooltip key={option.id} title={option.description} placement="top">
            {control}
          </Tooltip>
        ) : (
          <Box key={option.id}>{control}</Box>
        );
      })}
    </Box>
  );
}
